import React, { FC } from 'react';
import Icon from '@minecraft.icon';
import { FlexRow, BodyText, FlexCol } from '@minecraft.atoms';
import { Trans } from '@blocs.i18n';
import { DesignSystemTextColor } from '@minecraft.themes';
import { TemperatureValues } from '../enums/plans';
import { PlanFeatureType } from '../features';

export interface FeatureListProps {
  features: PlanFeatureType[];
  temperature: TemperatureValues;
  including?: string;
  className?: string;
  iconName?: string;
  isFullFeatureList?: boolean;
  justifyContent?: 'left' | 'center' | 'right';
  textColor?: DesignSystemTextColor;
}
interface FeatureListItemProps {
  feature: PlanFeatureType;
  temperature: TemperatureValues;
  value?: string;
  iconName?: string;
  textColor?: DesignSystemTextColor;
}

const FeatureListItem: FC<FeatureListItemProps> = ({
  feature,
  temperature,
  iconName = 'check-circle',
  textColor = 'default',
}) => {
  return (
    <FlexRow
      justifyContent="flex-start"
      alignItems="flex-start"
      className="cn_mb-3 cn_atom_pr-3"
      wrap="nowrap"
      gap="0"
      role="listitem"
      data-testid={`plan-feature-item-${feature.code}`}
    >
      <BodyText>
        <Icon name={iconName} className="cn_mr-3" themeColor={`${temperature}Paywall`} />
      </BodyText>
      <BodyText color={textColor}>
        <Trans i18nKey={feature.label} components={[<strong />]} values={{ size: feature.value }} />
      </BodyText>
    </FlexRow>
  );
};

export const FeatureList: FC<FeatureListProps> = ({
  including,
  features,
  temperature,
  iconName,
  className = '',
  isFullFeatureList = false,
  justifyContent = 'left',
  textColor,
}) => {
  return (
    <FlexRow wrap="nowrap" className={`cn_w-full ${className}`} gap="2" justifyContent={justifyContent}>
      <FlexCol
        role="list"
        aria-label={temperature}
        w="full"
        maxW="md"
        className={`cn_atom_py-2 ${isFullFeatureList ? 'cn_atom_px-0' : 'cn_atom_px-2'} `}
        bgColor={isFullFeatureList ? 'transparent' : `${temperature}PaywallFeaturesBg`}
        borderRadius="md"
      >
        {including && (
          <FlexRow
            alignItems="flex-start"
            justifyContent="flex-start"
            className="cn_atom_pb-2 cn_mb-3 cn_w-full"
            borderColor="default"
            borderPlacement="bottom"
          >
            <BodyText color="disabled" fontWeight="medium">
              {including}
            </BodyText>
          </FlexRow>
        )}
        {features?.map(
          (feature) =>
            feature.code &&
            !feature.flag && (
              <FeatureListItem
                key={feature.label}
                feature={feature}
                temperature={temperature}
                iconName={iconName}
                textColor={textColor}
              />
            )
        )}
      </FlexCol>
    </FlexRow>
  );
};
